import { z } from 'zod';

export const contactFormLimits = {
  nameMin: 3,
  nameMax: 50,
  telephoneMax: 40,
  messageMin: 8,
  messageMax: 500,
} as const;

type ContactFormValidationMessages = {
  emailRequired: string;
  emailInvalid: string;
  nameRequired: string;
  nameInvalid: string;
  nameTooShort: string;
  nameTooLong: string;
  telephoneTooLong: string;
  messageRequired: string;
  messageTooShort: string;
  messageTooLong: string;
  checkedRequired: string;
  honeypotTriggered: string;
};

export const createContactFormSchema = (messages: ContactFormValidationMessages) =>
  z.object({
    email: z
      .string({
        required_error: messages.emailRequired,
      })
      .trim()
      .min(1, messages.emailRequired)
      .email(messages.emailInvalid),
    name: z
      .string({
        required_error: messages.nameRequired,
        invalid_type_error: messages.nameInvalid,
      })
      .trim()
      .min(1, messages.nameRequired)
      .min(contactFormLimits.nameMin, messages.nameTooShort)
      .max(contactFormLimits.nameMax, messages.nameTooLong),
    telephone: z
      .string({
        invalid_type_error: messages.telephoneTooLong,
      })
      .trim()
      .max(contactFormLimits.telephoneMax, messages.telephoneTooLong)
      .optional(),
    message: z
      .string({
        required_error: messages.messageRequired,
      })
      .trim()
      .min(1, messages.messageRequired)
      .min(contactFormLimits.messageMin, messages.messageTooShort)
      .max(contactFormLimits.messageMax, messages.messageTooLong),
    checked: z
      .boolean({ required_error: messages.checkedRequired })
      .refine(value => value === true, {
        message: messages.checkedRequired,
      }),
    website: z
      .string({
        invalid_type_error: messages.honeypotTriggered,
      })
      .trim()
      .max(0, messages.honeypotTriggered)
      .optional(),
  });

export const serverContactFormSchema = createContactFormSchema({
  emailRequired: 'Please enter your email address.',
  emailInvalid: 'Please enter a valid email address.',
  nameRequired: 'Please enter your name.',
  nameInvalid: 'Please enter a valid name.',
  nameTooShort: 'Please enter at least 3 characters for your name.',
  nameTooLong: 'Please keep your name under 50 characters.',
  telephoneTooLong: 'Please keep the telephone number under 40 characters.',
  messageRequired: 'Please enter your message.',
  messageTooShort: 'Please add a little more detail to your message.',
  messageTooLong: 'Please keep your message under 500 characters.',
  checkedRequired: 'Please confirm that we may process your details to respond to your inquiry.',
  honeypotTriggered: 'Spam protection triggered.',
});

export type ContactFormData = z.infer<typeof serverContactFormSchema>;
